import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Box,
  Typography,
  TextField,
  Button,
  Link as MuiLink,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  Container
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff,
} from '@mui/icons-material';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import GoogleIcon from '@mui/icons-material/Google';
import GoogleLoginPopup from '../components/GoogleLoginPopup';
import { generateBrowserFingerprint, generateSimpleFingerprint } from '../utils/browserFingerprint';

export default function LoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null); // 'microsoft' or 'google'
  const [googlePopupOpen, setGooglePopupOpen] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: value || ''  // Fallback to empty string
    }));
  };

  // Add this function to test backend connection
  const testBackendConnection = async () => {
    try {
      const response = await fetch('https://propertyearn.net/api/api/health', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        console.log('Backend connection successful');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Backend connection failed:', error);
      return false;
    }
  };
  

// handleLogin function
const handleLogin = async (e) => {
  e.preventDefault();
  setError('');

  // Generate raw browser fingerprint before login
  let fingerprintData = null;
  
  try {
    // Try to generate comprehensive fingerprint first
    fingerprintData = await generateBrowserFingerprint();
    if (!fingerprintData) {
      // Fallback to simple fingerprint
      fingerprintData = generateSimpleFingerprint();
    }
    console.log('Raw browser fingerprint data:', fingerprintData);
  } catch (fpError) {
    console.warn('Could not generate browser fingerprint:', fpError);
    // Continue without fingerprint data
  }

  // Test backend connection first
  const isBackendConnected = await testBackendConnection();
  if (!isBackendConnected) {
    setError('Cannot connect to server. Please make sure the backend is running on port 5000.');
    return;
  }

  // Basic validation
  if (!formData.email) {
    setError('Email is required');
    return;
  }
  if (!formData.password) {
    setError('Password is required');
    return;
  }

  setLoading(true);

  try {
    const loginPayload = {
      email: formData.email,
      password: formData.password
    };

    // Add raw fingerprint data to login payload if available
    if (fingerprintData) {
      loginPayload.fingerprint_data = fingerprintData;
    }

    const response = await fetch('https://propertyearn.net/api/api/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginPayload)
    });

    // Rest of your existing login code remains the same...
    if (!response) {
      throw new Error('Cannot connect to server. Please check if the backend is running.');
    }

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      
      try {
        errorData = JSON.parse(errorText);
      } catch (parseError) {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }
      
      throw new Error(errorData.error || errorData.message || 'Login failed');
    }

    const data = await response.json();
    
    // Store the token in localStorage
    console.log('Login successful, token received:', data.token);
    localStorage.setItem('token', data.token);
    
    // Store fingerprint data reference if available
    if (fingerprintData) {
      localStorage.setItem('device_fingerprint_data', JSON.stringify(fingerprintData));
    }
    
    navigate('/dashboard');
    
  } catch (err) {
    console.error('Login error:', err);
    
    if (err.message.includes('Failed to fetch')) {
      setError('Cannot connect to server. Please make sure the backend is running on port 5000.');
    } else if (err.message.includes('NetworkError')) {
      setError('Network error. Please check your internet connection.');
    } else {
      setError(err.message || 'Login failed. Please try again.');
    }
  } finally {
    setLoading(false);
  }
};

  const handleMicrosoftLogin = async () => {
    setSocialLoading('microsoft');
    setError('');
    
    try {
      // Simulate Microsoft OAuth flow
      console.log('Initiating Microsoft login...');
      // In a real implementation, this would redirect to Microsoft OAuth
      // window.location.href = 'https://propertyearn.net/api/api/auth/microsoft';
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock successful login
      localStorage.setItem('token', 'mock-microsoft-token');
      navigate('/dashboard');
      
    } catch (err) {
      setError('Microsoft login failed. Please try again.');
    } finally {
      setSocialLoading(null);
    }
  };

  const handleGoogleLogin = () => {
    setError('');
    setGooglePopupOpen(true);
  };

  const handleGoogleLoginSuccess = (token) => {
    console.log('Google login successful with token:', token);
    localStorage.setItem('token', token);
    navigate('/dashboard');
  };

  const handleGooglePopupClose = () => {
    setGooglePopupOpen(false);
  };

  return (
    <>
      <Box sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        alignItems: 'center',
        py: 4
      }}>
        <Container maxWidth="sm">
          <Paper 
            elevation={8} 
            sx={{
              p: 4,
              borderRadius: 3,
              backgroundColor: 'white'
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  fontWeight: 700,
                  mb: 1,
                  color: 'primary.main'
                }}
              >
                Welcome Back
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '1.1rem'
                }}
              >
                Sign in to your PropertyEarn account
              </Typography>
            </Box>
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  borderRadius: 2,
                  backgroundColor: 'error.light',
                  color: 'error.dark'
                }}
              >
                {error}
              </Alert>
            )}
            
            {/* Social Login Buttons */}
            <Stack spacing={2} sx={{ mb: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleMicrosoftLogin}
                disabled={socialLoading !== null}
                startIcon={
                  socialLoading === 'microsoft' ? 
                    <CircularProgress size={20} /> : 
                    <MicrosoftIcon />
                }
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: 'grey.300',
                  color: 'text.primary',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.light',
                    color: 'primary.main'
                  }
                }}
              >
                {socialLoading === 'microsoft' ? 'Signing in...' : 'Continue with Microsoft'}
              </Button>
              
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleGoogleLogin}
                disabled={socialLoading !== null}
                startIcon={<GoogleIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: 'grey.300',
                  color: 'text.primary',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.light',
                    color: 'primary.main'
                  }
                }}
              >
                Continue with Google
              </Button>
            </Stack>
            
            {/* Divider */}
            <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
              <Divider sx={{ flexGrow: 1 }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  px: 2, 
                  color: 'text.secondary',
                  fontWeight: 500
                }}
              >
                OR
              </Typography>
              <Divider sx={{ flexGrow: 1 }} />
            </Box>
            
            {/* Email/Password Form */}
            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                variant="outlined"
                margin="normal"
                value={formData.email}
                onChange={handleChange}
                required
                type="email"
                disabled={loading || socialLoading !== null}
                inputProps={{ maxLength: 100 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <TextField
                fullWidth
                label="Password"
                name="password"
                variant="outlined"
                margin="normal"
                value={formData.password}
                onChange={handleChange}
                required
                type={showPassword ? 'text' : 'password'}
                disabled={loading || socialLoading !== null}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading || socialLoading !== null}
                        aria-label="toggle password visibility"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <MuiLink 
                  component={Link} 
                  to="/forgot-password" 
                  variant="body2"
                  underline="hover"
                  sx={{ 
                    fontWeight: 500,
                    color: 'primary.main'
                  }}
                >
                  Forgot password?
                </MuiLink>
              </Box>
              
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                disabled={loading || socialLoading !== null}
                sx={{ 
                  mt: 3, 
                  py: 1.5,
                  height: 48,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 4
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In with Email'
                )}
              </Button>
            </form>
            
            <Typography 
              variant="body2" 
              sx={{ 
                mt: 3, 
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              Don't have an account?{' '}
              <MuiLink 
                component={Link} 
                to="/signup" 
                fontWeight="bold"
                underline="hover"
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Sign up
              </MuiLink>
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* Google Login Popup */}
      <GoogleLoginPopup 
        open={googlePopupOpen}
        onClose={handleGooglePopupClose}
        onSuccess={handleGoogleLoginSuccess}
      />
    </>
  );
}